import React, {useEffect, useMemo, useState} from 'react';
import TablePaginationBar from '../../../components/TablePaginationBar';
import ParcelDetailDrawer from './components/ParcelDetailDrawer';
import {ParcelInitialValue} from '../../../config/filterOptions';
import {ParcelColumns} from './components/ParcelColumns';
import ParcelFilter from './components/ParcelFilter';
import {useParcelQuery} from "./hooks/useParcelQuery";
import {getUserInfo} from "../../../utils/auth";
import { useWarehouseContext } from '../../../contexts/WarehouseContext';
import {useHashState} from 'utils/hashState';
import {OrderType} from "../../../graphql/types/OrderType";
import { getLastWeekDateTimeRange } from '../../../utils/dateTimeUtils';

const ParcelsPage: React.FC = () => {
    const [detailVisible, setDetailVisible] = useState(false);
    const [orderId, setOrderId] = useState<string>('');
    const [tempFilters, setTempFilters] = useHashState(ParcelInitialValue);
    const [filters, setFilters] = useHashState(ParcelInitialValue);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    // 当前页表格展示用的选中 key
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    // 跨分页持久化的选中 key 集合
    const [persistentSelection, setPersistentSelection] = useState<React.Key[]>([]);
    // 跨分页缓存的选中行数据
    const [selectedRowMap, setSelectedRowMap] = useState<Map<React.Key, OrderType>>(new Map());
    const [selectedRows, setSelectedRows] = useState<OrderType[]>([]);
    const { currentWarehouse } = useWarehouseContext();
    const warehouseId = currentWarehouse?.id || null;
    const [sortField, setSortField] = useState<string | null>(null);
    const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC' | null>(null);

    // 默认时间范围不写入 URL：只在页面加载时计算一次，避免每次 render 都生成新时间导致无限 refetch
    const defaultOrderTime = useMemo(() => getLastWeekDateTimeRange(), []);

    const getEffectiveOrderTime = (orderTime: any) => {
        // null：用户手动清空（点击叉号），保持为空，不回填默认值
        if (orderTime === null) return null;
        const isValid = orderTime &&
            Array.isArray(orderTime) &&
            orderTime.length === 2 &&
            orderTime[0] !== null &&
            orderTime[1] !== null;
        return isValid ? orderTime : defaultOrderTime;
    };

    const {
        parcelData,
        loading,
        refetch,
        totalElements,
        buildQueryVariables
    } = useParcelQuery(
        {
            ...filters,
            orderTime: getEffectiveOrderTime(filters.orderTime)
        },
        currentPage,
        pageSize,
        warehouseId,
        sortField,
        sortDirection
    );

    const handleSelectChange = (newSelectedRowKeys: React.Key[], newSelectedRows: OrderType[]) => {
        // 1. 更新当前页受控选中 key
        setSelectedRowKeys(newSelectedRowKeys);

        // 2. 维护跨页持久化选中 key
        setPersistentSelection(prevKeys => {
            const nextKeys = [...prevKeys];

            parcelData.forEach(order => {
                const key: React.Key = order.trackingNumber || order.id;
                const isSelectedOnPage = newSelectedRowKeys.includes(key);

                if (isSelectedOnPage) {
                    if (!nextKeys.includes(key)) {
                        nextKeys.push(key);
                    }
                } else {
                    const idx = nextKeys.indexOf(key);
                    if (idx > -1) {
                        nextKeys.splice(idx, 1);
                    }
                }
            });

            return nextKeys;
        });

        // 3. 维护跨页缓存的选中行数据
        setSelectedRowMap(prevMap => {
            const nextMap = new Map(prevMap);

            parcelData.forEach(order => {
                const key: React.Key = order.trackingNumber || order.id;
                const isSelectedOnPage = newSelectedRowKeys.includes(key);

                if (isSelectedOnPage) {
                    nextMap.set(key, order);
                } else {
                    nextMap.delete(key);
                }
            });

            return nextMap;
        });
    };

    const handleResetFilters = () => {
        setTempFilters(ParcelInitialValue);
        setFilters(ParcelInitialValue);
    };

    const handleFilter = () => {
        // 直接使用 tempFilters 的值来更新 filters，确保 hashState 同步
        // 需要明确设置所有字段，包括要删除的字段（设为 undefined）
        // 这样 updateHashState 才能包含这些字段，encodeHash 会过滤它们，URL hash 中不会包含
        const newFilters: any = {...tempFilters};
        // 确保 filters 中所有可能的字段都被包含，即使值是 undefined 或 null
        // 如果 tempFilters 中某个字段不存在，但 filters 中有，需要明确设置为 undefined
        // 这样可以确保旧字段被包含在更新中，encodeHash 会过滤它们
        const allKeys = new Set([...Object.keys(tempFilters), ...Object.keys(filters)]);
        allKeys.forEach(key => {
            if (!(key in newFilters)) {
                newFilters[key] = undefined;
            }
        });
        // updateHashState 会保留 undefined 字段，encodeHash 会过滤它们，URL hash 中不会包含已删除的字段
        setFilters(newFilters);
        setCurrentPage(1);
    };

    // 添加useEffect监听状态变化后执行查询
    useEffect(() => {
        if (warehouseId !== null) {
            refetch(buildQueryVariables());
        }
    }, [filters, currentPage, pageSize, warehouseId, sortField, sortDirection]);

    // 当筛选条件或仓库变化时清空选中状态（跨页选择也一并清空）
    useEffect(() => {
        setSelectedRowKeys([]);
        setPersistentSelection([]);
        setSelectedRowMap(new Map());
        setSelectedRows([]);
    }, [filters, warehouseId]);

    // 数据刷新后，根据持久化选择恢复当前页的勾选状态
    useEffect(() => {
        if (loading || !parcelData || parcelData.length === 0) {
            return;
        }

        const keysOnCurrentPage: React.Key[] = [];
        parcelData.forEach(order => {
            const key: React.Key = order.trackingNumber || order.id;
            if (persistentSelection.includes(key)) {
                keysOnCurrentPage.push(key);
            }
        });

        setSelectedRowKeys(keysOnCurrentPage);
    }, [parcelData, loading, persistentSelection]);

    // 数据刷新时，把当前页中已持久选中的行补齐到缓存 Map 中
    useEffect(() => {
        if (loading || !parcelData || parcelData.length === 0) {
            return;
        }

        setSelectedRowMap(prevMap => {
            const nextMap = new Map(prevMap);

            parcelData.forEach(order => {
                const key: React.Key = order.trackingNumber || order.id;
                if (persistentSelection.includes(key)) {
                    nextMap.set(key, order);
                }
            });

            return nextMap;
        });
    }, [parcelData, loading, persistentSelection]);

    // 统一由 Map 导出选中行数组，供筛选区域和操作按钮使用
    useEffect(() => {
        setSelectedRows(Array.from(selectedRowMap.values()));
    }, [selectedRowMap]);

    const showParcelDetail = (record: OrderType) => {
        setOrderId(record.id);
        setDetailVisible(true);
    };
    
    // 处理分页变化
    const handlePageChange = (page: number, pageSize: number) => {
        setCurrentPage(page);
        setPageSize(pageSize);
    };

    return (
        <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
                {/* 筛选条件 */}
                <ParcelFilter
                    filters={tempFilters}
                    setFilters={setTempFilters}
                    handleFilter={handleFilter}
                    handleResetFilters={handleResetFilters}
                    selectedRows={selectedRows}
                    warehouseId={warehouseId}
                />

                {/* 订单列表 */}
                <ParcelColumns
                    parcelData={parcelData}
                    showParcelDetail={showParcelDetail}
                    onSelectChange={handleSelectChange}
                    selectedRowKeys={selectedRowKeys}
                    loading={loading}
                    onSortChange={(field, direction) => { setSortField(field); setSortDirection(direction); }}
                    sortField={sortField}
                    sortDirection={sortDirection}
                />

                {/* 分页 */}
                <TablePaginationBar
                    total={totalElements}
                    current={currentPage}
                    pageSize={pageSize}
                    onChange={handlePageChange}
                />

                {/* 包裹详情抽屉组件 */}
                <ParcelDetailDrawer
                    visible={detailVisible}
                    onClose={() => setDetailVisible(false)}
                    orderId={orderId}
                    userRole={getUserInfo()?.account?.role?.name || ''}
                    onRefreshList={() => refetch(buildQueryVariables())}
                />
            </div>
    );
};

export default ParcelsPage;
